// CommandPaletteHost — the single root ⌘K palette for the whole workbench.
//
// It merges two command feeds and renders the shared CommandPalette view:
//   1. Declarative plugin `commands` (from the registry), gated by `when(ctx)`
//      against the live host capabilities and adapted to the view shape — a
//      contribution whose `run(ctx)` returns a PromptStep drives the two-step
//      prompt.
//   2. Context-derived commands from surfaces that registered a command source
//      (e.g. the process editor, whose commands need its live step-edit api).
//
// ⌘K is claimed through the hotkey registry (global scope, first-wins); each
// plugin command's optional `hotkey` is claimed the same way (a duplicate chord
// is ignored + dev-warned by the registry). SSR/headless-safe: the palette only
// renders after open, and the hotkey listener is the registry's (window-guarded).

import { useEffect, useState, type ReactElement } from "react";
import { CommandPalette, type PaletteCommand } from "./CommandPalette.js";
import { useCommandContributions } from "./PluginRegistryContext.js";
import { useCommandSourceRegistry } from "./commandSources.js";
import { useHotkey, useHotkeyRegistry } from "./hotkeys.js";
import { isActionable, useWorkbenchHost, type WorkbenchHostContext } from "./hostContext.js";
import type { CommandContribution } from "./types.js";

/** Adapt a declarative contribution to a view command. `run` invokes the
 *  contribution against the live ctx: a returned PromptStep becomes the view's
 *  dynamic prompt; void becomes an immediate close. */
function toPaletteCommand(cmd: CommandContribution, ctx: WorkbenchHostContext): PaletteCommand {
  return {
    id: cmd.id,
    title: cmd.title,
    ...(cmd.keywords ? { keywords: cmd.keywords } : {}),
    ...(cmd.group ? { group: cmd.group } : {}),
    ...(cmd.danger ? { danger: cmd.danger } : {}),
    run: () => {
      const step = cmd.run(ctx);
      if (step) return { label: step.label, commit: step.commit };
      return true;
    },
  };
}

export function CommandPaletteHost(): ReactElement {
  const [open, setOpen] = useState(false);
  const ctx = useWorkbenchHost();
  const pluginCommands = useCommandContributions();
  const sources = useCommandSourceRegistry();
  const registry = useHotkeyRegistry();

  // ⌘K opens the palette (global; first-wins so a stray plugin ⌘K is ignored).
  useHotkey("mod+k", "global", () => setOpen(true));

  // Each plugin command's optional hotkey — registered directly (a variable-length
  // list can't go through the useHotkey hook). Fires the command immediately when
  // its `when` gate passes; a hotkeyed command is expected to be immediate.
  useEffect(() => {
    const unregs = pluginCommands
      .filter((o) => o.contribution.hotkey)
      .map((o) =>
        registry.register(o.contribution.hotkey!, "global", () => {
          if (isActionable(o.contribution, ctx)) o.contribution.run(ctx);
        }),
      );
    return () => unregs.forEach((u) => u());
  }, [registry, pluginCommands, ctx]);

  // Build the merged, when-gated command list — only while open (cheap; the
  // sources read live state at call time).
  const commands: readonly PaletteCommand[] = open
    ? [
        ...pluginCommands
          .filter((o) => isActionable(o.contribution, ctx))
          .map((o) => toPaletteCommand(o.contribution, ctx)),
        ...sources.collect(ctx),
      ]
    : [];

  return <CommandPalette open={open} onClose={() => setOpen(false)} commands={commands} dataName="command-palette" />;
}
